import React, { useEffect, useState } from "react";
import { GitBranch, AlertTriangle, CheckCircle2, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";
import {
    dosyaDurumuRules,
    dosyaDurumuTransition,
    dosyaDurumuValidate,
} from "../lib/api";

const TIP_LABELS = { TAHKIM: "Tahkim", ICRA: "İcra", DAVA: "Dava" };

export default function DosyaDurumuWorkflowPanel({
    dosyaId,
    dosyaTipi = "TAHKIM",
    currentDurum,
    dosyaDurumuLabel,
    gun = 0,
    onTransition,
}) {
    const [rules, setRules] = useState([]);
    const [target, setTarget] = useState("");
    const [validation, setValidation] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        dosyaDurumuRules(dosyaTipi)
            .then((d) => setRules(d.rules || []))
            .catch(() => {});
    }, [dosyaTipi]);

    const applicable = rules.filter((r) => r.from === (currentDurum || "YENI"));

    async function checkStatus(next) {
        setTarget(next);
        if (!next || next === currentDurum) {
            setValidation(null);
            return;
        }
        setLoading(true);
        try {
            const res = await dosyaDurumuValidate({
                dosya_id: dosyaId,
                new_dosya_durumu: next,
                old_dosya_durumu: currentDurum,
            });
            setValidation(res);
        } catch (e) {
            setValidation({
                ok: false,
                errors: [e?.response?.data?.detail || "Doğrulama hatası"],
            });
        } finally {
            setLoading(false);
        }
    }

    async function applyTransition(force = false) {
        if (!target || target === currentDurum) return;
        setLoading(true);
        try {
            await dosyaDurumuTransition({
                dosya_id: dosyaId,
                new_dosya_durumu: target,
                force,
            });
            toast.success("Dosya durumu güncellendi.");
            setValidation(null);
            setTarget("");
            onTransition?.();
        } catch (e) {
            const wf = e?.response?.data;
            const errs = wf?.errors || wf?.detail || ["Geçiş reddedildi"];
            setValidation(wf || { ok: false, errors: errs });
            toast.error(Array.isArray(errs) ? errs[0] : errs);
        } finally {
            setLoading(false);
        }
    }

    const tierColor =
        gun >= 30 ? "text-rose-400" : gun >= 21 ? "text-orange-400" : gun >= 14 ? "text-amber-400" : "text-slate-400";

    return (
        <section className="rounded-xl border border-emerald-500/25 bg-[#121A2F]/80 p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
                <GitBranch className="h-5 w-5 text-emerald-400" />
                <h3 className="font-display text-lg text-white">Detaylı Durum Akışı</h3>
                <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-500">
                    {TIP_LABELS[dosyaTipi] || dosyaTipi}
                </span>
            </div>
            <div className="flex flex-wrap items-center gap-2 mb-3 text-sm">
                <span className="text-slate-400">Durum:</span>
                <span className="text-white font-medium">{dosyaDurumuLabel || currentDurum}</span>
                {gun > 0 && (
                    <span className={`flex items-center gap-1 text-xs ${tierColor}`}>
                        <Clock className="h-3.5 w-3.5" />
                        {gun} gündür bu adımda
                        {gun >= 14 && " 🔴"}
                    </span>
                )}
            </div>
            <p className="text-xs text-slate-500 mb-2">Geçerli adımlar:</p>
            <div className="flex flex-wrap gap-2 mb-3">
                {applicable.map((r) => (
                    <button
                        key={r.to}
                        type="button"
                        onClick={() => checkStatus(r.to)}
                        className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${
                            target === r.to
                                ? "border-emerald-400 bg-emerald-400/15 text-emerald-200"
                                : "border-white/10 text-slate-400 hover:border-white/20"
                        }`}
                    >
                        → {r.to_label}
                    </button>
                ))}
                {!applicable.length && (
                    <span className="text-xs text-slate-500">Son adım veya kapatıldı.</span>
                )}
            </div>
            {loading && (
                <div className="flex items-center gap-2 text-sm text-slate-400 mb-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Kontrol ediliyor…
                </div>
            )}
            {validation && !validation.ok && (
                <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 mb-3 text-sm">
                    <div className="flex items-start gap-2 text-rose-300">
                        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                        <ul className="list-disc pl-4 space-y-1">
                            {(validation.errors || []).map((e, i) => (
                                <li key={i}>{e}</li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}
            {validation?.ok && target && target !== currentDurum && (
                <div className="flex items-center gap-2 text-emerald-400 text-sm mb-3">
                    <CheckCircle2 className="h-4 w-4" /> Geçiş kuralları sağlanıyor
                </div>
            )}
            <div className="flex gap-2">
                <button
                    type="button"
                    disabled={!target || loading || target === currentDurum}
                    onClick={() => applyTransition(false)}
                    className="h-9 px-4 rounded-md bg-emerald-500 text-slate-900 text-xs font-semibold disabled:opacity-40"
                >
                    Alt durumu uygula
                </button>
                <button
                    type="button"
                    disabled={!target || loading}
                    onClick={() => applyTransition(true)}
                    className="h-9 px-3 rounded-md border border-amber-500/40 text-amber-300 text-xs"
                >
                    Zorla geç
                </button>
            </div>
        </section>
    );
}
